import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/CustomerProfile.css";

function CustomerProfile() {
    const location = useLocation();
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [customerNumber, setCustomerNumber] = useState("");
    const [accountInfo, setAccountInfo] = useState(null);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [attempts, setAttempts] = useState(0);
    const [loading, setLoading] = useState(true);
    const endPoint = process.env.REACT_APP_API_URL;

    const getCookie = (name) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i].trim();
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return null;
    };

    const getStatusColor = (status) => {
        if (!status) return "black";
        const code = status.trim().charAt(0);
        return ["1", "3", "4"].includes(code) ? "green" : "red";
    };

    const displayMatchText = (status) =>
        status ? status.replace(/^\d+:\s*/, "") : "";

    const getActionType = (accountInfo) => {
        if (!accountInfo?.nameMatchStatus) return "NONE";
        const code = accountInfo.nameMatchStatus.trim().charAt(0);
        if (["1", "3", "4"].includes(code)) return "CONFIRM";
        if (code === "2") return "REVIEW";
        return "NONE";
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        let sessionId = params.get("s") || params.get("sessionId");
        const dataParam = params.get("data");

        if (sessionId) {
            sessionStorage.setItem("sessionId", sessionId);
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }

        if (!sessionId) {
            sessionId = getCookie("sessionId");
        }

        if (!sessionId) {
            sessionId = sessionStorage.getItem("sessionId");
        }

        if (sessionId) {
            fetchUserData(sessionId);
        } else if (dataParam) {
            try {
                const parsedData = JSON.parse(decodeURIComponent(dataParam));
                setUserData(parsedData);
                setLoading(false);
            } catch (err) {
                setError("Failed to parse user data");
                setLoading(false);
            }
        } else {
            setError("No session found. Please try logging in again.");
            setTimeout(() => navigate("/"), 3000);
            setLoading(false);
        }
    }, [location.search]);

    const fetchUserData = async (sessionId) => {
        try {
            setLoading(true);
            setError("");
            
            const url = `${endPoint}getUserData?sessionId=${sessionId}`;
            
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setUserData(data);
            } else if (response.status === 404) {
                setError("Session not found or expired. Please try again.");
                sessionStorage.removeItem("sessionId");
                setTimeout(() => navigate("/"), 3000);
            } else if (response.status === 410) {
                setError("Session expired. Please try again.");
                sessionStorage.removeItem("sessionId");
                setTimeout(() => navigate("/"), 3000);
            } else {
                setError(`Failed to load user data: ${response.status}`);
            }
        } catch (err) {
            setError("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCheck = async () => {
        setError("");
        setAccountInfo(null);
        setSuccessMsg("");
        const cleaned = customerNumber.trim();
        if (!cleaned) {
            setError("Please enter Account or Customer Number");
            return;
        }

        let payload = {
            name: userData.name || "",
            gender: userData.gender || "",
            phoneNumber: userData.phone_number || "",
            psut: userData.sub || "",
            email: userData.email || "",
            nationality: userData.nationality || "",
            region: userData.address?.region || "",
            zone: userData.address?.zone || "",
            woreda: userData.address?.woreda || "",
            picture: userData.picture || "",
        };

        if (/^\d{13}$/.test(cleaned)) {
            payload.accountNumber = cleaned;
        } else {
            payload.cusNum = cleaned;
        }

        try {
            const res = await fetch(`${endPoint}lookupInPrOb`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            setAccountInfo(data);
            setAttempts((prev) => prev + 1);
        } catch (err) {
            setError("Server error");
        }
    };

    const handleSubmit = async (type) => {
        if (!accountInfo) return;
        const picture1 = userData.picture || "";
        const picture2 = picture1 ? picture1.replace("https://obfayda.oromiabank.com", "https://obfayda.ob.com") : "";

        const payloads = {
            psut: userData.sub,
            name: userData.name,
            nationality: userData.nationality,
            gender: userData.gender,
            phoneNumber: userData.phone_number,
            birthdate: userData.birthdate,
            email: userData.email,
            region: userData.address?.region,
            zone: userData.address?.zone,
            woreda: userData.address?.woreda,
            picture: picture1,
            picture2: picture2,
            bankName: accountInfo.customerName,
            bankNationality: accountInfo.nationality,
            bankGender: accountInfo.gender,
            branch: accountInfo.branch,
            match: accountInfo.nameMatchStatus,
            customerNumber: accountInfo.customerNumber,
            accountNumber: accountInfo.accountNumber || accountInfo.customerNumberToAccount
        };

        try {
            const res = await fetch(`${endPoint}confirmPrOb`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payloads),
            });
            if (res.ok) {
                setSuccessMsg(type === "CONFIRM" ? "Your NID information has been successfully linked to your account. Thank you!" : "Your information has been submitted for review. We will update you soon.");
                setShowPopup(true);
                setTimeout(() => { setShowPopup(false); navigate("/"); }, 10000);
            } else {
                setError("Failed to send data");
            }
        } catch {
            setError("Server error");
        }
    };

    if (loading) {
        return (
            <div className="profile-container">
                <div className="profile-card">
                    <div className="loading-spinner">Loading your profile data...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-container">
                <div className="profile-card">
                    <div className="error-message" style={{textAlign: 'center', padding: '20px'}}>
                        <h3>Error</h3>
                        <p>{error}</p>
                        <button onClick={() => navigate("/")} style={{marginTop: '10px', padding: '8px 16px', cursor: 'pointer'}}>
                            Go Home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="profile-container">
                <div className="profile-card">
                    <p>No user data available. Please try again.</p>
                    <button onClick={() => navigate("/")}>Go Home</button>
                </div>
            </div>
        );
    }

    const actionType = getActionType(accountInfo);

    return (
        <div className="profile-container">
            <div className="profile-card">
                <div className="profile-header">
                    <div className="profile-picture">
                        {userData.picture ? <img src={userData.picture} alt="User" /> : <div className="no-img">No Image</div>}
                    </div>
                    <h2>{userData.name}</h2>
                    <p className="user-id">PSTU: {userData.sub}</p>
                </div>

                <div className="profile-details-grid">
                    <div className="detail-item"><strong>Gender:</strong> {userData.gender}</div>
                    <div className="detail-item"><strong>Phone:</strong> {userData.phone_number}</div>
                    <div className="detail-item"><strong>Nationality:</strong> {userData.nationality}</div>
                    <div className="detail-item"><strong>Birthdate:</strong> {userData.birthdate}</div>
                    <div className="detail-item"><strong>Zone:</strong> {userData.address?.zone}</div>
                </div>

                <div className="lookup-section">
                    <input
                        placeholder="Account or Customer Number"
                        value={customerNumber}
                        onChange={(e) => {
                            setCustomerNumber(e.target.value.replace(/\s+/g, ""));
                            setAttempts(0);
                        }}
                    />
                    <button className="check-btn" onClick={handleCheck}>Check</button>
                </div>

                {error && <p className="error-message">{error}</p>}

                {accountInfo && (
                    <div className="bank-info-box">
                        {accountInfo.customerName ? (
                            <>
                                <h3>Bank Details</h3>
                                <div className="bank-data-grid">
                                    <p><strong>Name:</strong> {accountInfo.customerName}</p>
                                    <p><strong>A/C:</strong> {accountInfo.accountNumber}</p>
                                    <p><strong>Bank Gender:</strong> {accountInfo.gender}</p>
                                    <p><strong>Bank Nationality:</strong> {accountInfo.nationality}</p>
                                    <p><strong>Branch Code:</strong> {accountInfo.branch}</p>
                                    <p><strong>Branch Name:</strong> {accountInfo.branchName}</p>
                                </div>

                                {/* Display detailed match status */}
                                {accountInfo.nameMatchDetail && (
                                    <p className="status-text" style={{ color: getStatusColor(accountInfo.nameMatchStatus) }}>
                                        <strong>Name:</strong> {displayMatchText(accountInfo.nameMatchDetail)}
                                    </p>
                                )}
                                {accountInfo.genderMatchDetail && (
                                    <p className="status-text" style={{ color: getStatusColor(accountInfo.nameMatchStatus) }}>
                                        <strong>Gender:</strong> {displayMatchText(accountInfo.genderMatchDetail)}
                                    </p>
                                )}
                                {accountInfo.nationalityMatchDetail && (
                                    <p className="status-text" style={{ color: getStatusColor(accountInfo.nameMatchStatus) }}>
                                        <strong>Nationality:</strong> {displayMatchText(accountInfo.nationalityMatchDetail)}
                                    </p>
                                )}
                                
                                <p className="status-text" style={{ color: getStatusColor(accountInfo.nameMatchStatus), fontWeight: 'bold', marginTop: '10px' }}>
                                    {displayMatchText(accountInfo.nameMatchStatus)}
                                </p>

                                <div className="button-group">
                                    {actionType === "CONFIRM" && (
                                        <button className="btn confirm" onClick={() => handleSubmit("CONFIRM")}>Confirm</button>
                                    )}
                                    {actionType === "REVIEW" && (
                                        <button 
                                            className="btn review" 
                                            onClick={() => handleSubmit("REVIEW")}
                                            disabled={attempts < 2}
                                        >
                                            Apply For Review {attempts < 2 ? `(${attempts}/2)` : ""}
                                        </button>
                                    )}
                                    <button className="btn cancel" onClick={() => navigate("/")}>Cancel</button>
                                </div>
                            </>
                        ) : (
                            <p className="error-message">{displayMatchText(accountInfo.nameMatchStatus)}</p>
                        )}
                    </div>
                )}
            </div>

            {showPopup && (
                <div className="popup-box toast">
                    <h3>Success</h3>
                    <p>{successMsg}</p>
                </div>
            )}
        </div>
    );
}

export default CustomerProfile;